import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { peek } from '../api.js';
import { texts, useLang } from '../i18n.js';
import { navigate } from '../router.js';
import { AvatarStack, MoreOptions, RowMenu, toast } from '../ui.js';
import { FoldHead, useFold } from '../fold.js';

/**
 * The tasks per step (owner 2026-10-31, thread "Visa uppgifter per steg i projektets progress"): each step of "Var
 * projektet är" says how many of its tasks are done, and a click lists them. A round tick = ticked by the Hub, a square
 * = ticked by hand, "1/3" = part of the videos. Four states only: Klar, Din tur, Väntar på X, Ej relevant. Questions
 * ("Behövs resa?") are the producer's, answered Ja/Nej. "Ej relevant" is in each row's ⋯, the optional tasks under Fler
 * val. The tasks never move the step: it still comes from the videos.
 */

export type TaskStage = 'planning' | 'editing' | 'internal_review' | 'with_customer' | 'delivered';
type Action = 'done' | 'undone' | 'yes' | 'no' | 'not_relevant' | 'relevant';
interface Pill {
  n: number;
  state: 'approved' | 'changes' | 'client' | 'now' | 'todo';
  extra: boolean;
}
export interface TaskView {
  key: string;
  stage: TaskStage;
  role: string;
  title: string;
  question: string | null;
  how: string;
  state: 'done' | 'not_relevant' | 'mine' | 'waiting' | 'todo' | 'question';
  detail: string | null;
  who: { id: string; name: string }[];
  progress: { done: number; total: number } | null;
  videos: { id: string; name: string; done: boolean }[] | null;
  drafts: { id: string; name: string; pills: Pill[]; status: string }[] | null;
  actions: Action[];
}
export interface StageTasks {
  stage: TaskStage;
  done: number;
  total: number;
  tasks: TaskView[];
}
export interface TaskList {
  current: TaskStage | null;
  stages: StageTasks[];
  optional: { key: string; title: string; on: boolean }[];
  draftsIncluded: number;
}

const T = texts(
  {
    count: (d: number, n: number) => `${d} of ${n} done`,
    countShort: (d: number, n: number) => `${d}/${n}`,
    yourTasks: 'Your tasks',
    others: 'Others in the step',
    done: 'Done',
    yes: 'Yes',
    no: 'No',
    undo: 'Undo',
    notRelevant: 'Not relevant',
    rowMenu: (title: string) => `More for ${title}`,
    yourTurn: 'Your turn',
    draft: (n: number) => `Draft ${n}`,
    pill: { approved: 'approved', changes: 'changes', client: 'with the client', now: 'being made', todo: 'not started' } as Record<Pill['state'], string>,
    extra: 'beyond the agreement',
    optionalTitle: 'Optional tasks',
    optionalHint: 'Switch on what this project needs; it shows in its step.',
    legendAuto: 'Ticked by the Hub',
    legendHand: 'Ticked by hand',
    legendPart: 'Part of the videos',
    legendNa: 'Not relevant',
    failed: 'The tasks could not be loaded.',
    myTitle: 'Tasks in your projects',
    answer: 'Answer',
    nQuestions: (n: number) => (n === 1 ? '1 question' : `${n} questions`),
    showAll: (n: number) => `Show all ${n}`,
    showFewer: 'Show fewer'
  },
  {
    count: (d: number, n: number) => `${d} av ${n} klara`,
    countShort: (d: number, n: number) => `${d}/${n}`,
    yourTasks: 'Dina uppgifter',
    others: 'Övriga i steget',
    done: 'Klar',
    yes: 'Ja',
    no: 'Nej',
    undo: 'Ångra',
    notRelevant: 'Ej relevant',
    rowMenu: (title: string) => `Mer för ${title}`,
    yourTurn: 'Din tur',
    draft: (n: number) => `Utkast ${n}`,
    pill: { approved: 'godkänt', changes: 'ändringar', client: 'hos kunden', now: 'görs nu', todo: 'inte påbörjat' },
    extra: 'utöver avtalet',
    optionalTitle: 'Valfria uppgifter',
    optionalHint: 'Slå på det som projektet behöver, så syns det i sitt steg.',
    legendAuto: 'Bockas av Hubben',
    legendHand: 'Bockas för hand',
    legendPart: 'Del av videorna',
    legendNa: 'Ej relevant',
    failed: 'Uppgifterna gick inte att hämta.',
    myTitle: 'Uppgifter i dina projekt',
    answer: 'Svara',
    nQuestions: (n: number) => (n === 1 ? '1 fråga' : `${n} frågor`),
    showAll: (n: number) => `Visa alla ${n}`,
    showFewer: 'Visa färre'
  }
);

const HAND = new Set(['script', 'travel', 'gear', 'subtitles', 'extras', 'kickoff', 'planningMeeting', 'ideas', 'feedbackMeeting', 'renewal']);

/** The project's tasks, loaded once per change of the project (`rev`), and the call that changes one. */
export function useStageTasks(api: Api, projectId: string, rev: string) {
  const path = `/api/projects/${projectId}/tasks`;
  const [list, setList] = useState<TaskList | null | 'failed'>(() => peek<TaskList>(api, path) ?? null);
  useEffect(() => {
    let live = true;
    api.get<TaskList>(path).then(
      (r) => live && setList(r),
      () => live && setList((x) => (x && x !== 'failed' ? x : 'failed'))
    );
    return () => {
      live = false;
    };
  }, [projectId, rev]);
  const act = async (key: string, action: Action | 'on' | 'off') => {
    try {
      setList(await api.post<TaskList>(`${path}/${key}`, { action }));
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  return { list: list === 'failed' ? null : list, failed: list === 'failed', act };
}

function Mark(p: { t: TaskView }) {
  const { t } = p;
  const hand = HAND.has(t.key);
  if (t.state === 'not_relevant') return <span class="tk-box tk-na" aria-hidden="true">–</span>;
  if (t.state === 'question') return <span class="tk-box tk-q" aria-hidden="true">?</span>;
  if (t.state === 'done') return <span class={`tk-box tk-on${hand ? '' : ' tk-auto'}`} aria-hidden="true">✓</span>;
  if (t.progress && t.progress.done > 0) return <span class="tk-box tk-auto tk-part" aria-hidden="true">{`${t.progress.done}/${t.progress.total}`}</span>;
  return <span class={`tk-box${hand ? '' : ' tk-auto'}`} aria-hidden="true" />;
}

function Drafts(p: { t: TaskView }) {
  const lang = useLang();
  const x = T[lang];
  return (
    <div class="tk-drafts">
      {(p.t.drafts ?? []).map((d) => (
        <div class="tk-dr" key={d.id}>
          <b>{d.name}</b>
          <span class="tk-pills">
            {d.pills.map((pl) => (
              <span class={`tk-pill is-${pl.state}${pl.extra ? ' is-extra' : ''}`} title={`${x.draft(pl.n)}: ${x.pill[pl.state]}${pl.extra ? ` · ${x.extra}` : ''}`}>
                {x.draft(pl.n)}
                {pl.state === 'approved' || pl.state === 'changes' ? ' ✓' : ''}
                {pl.extra ? <span class="sr-only"> ({x.extra})</span> : null}
              </span>
            ))}
          </span>
          <span class="muted small">{d.status}</span>
        </div>
      ))}
    </div>
  );
}

function TaskRow(p: { t: TaskView; act: (key: string, a: Action) => void }) {
  const lang = useLang();
  const x = T[lang];
  const { t } = p;
  const has = (a: Action) => t.actions.includes(a);
  return (
    <li class={`tk-row is-${t.state}`}>
      <Mark t={t} />
      <div class="tk-body">
        <div class="tk-title">{t.question ?? t.title}</div>
        <div class="tk-meta">
          {t.state === 'not_relevant' || t.state === 'done' ? t.detail : [t.detail, t.state === 'question' ? null : t.how].filter(Boolean).join(' · ')}
          {has('relevant') || has('undone') ? (
            <>
              {' · '}
              <button type="button" class="linklike" onClick={() => p.act(t.key, has('relevant') ? 'relevant' : 'undone')}>
                {x.undo}
              </button>
            </>
          ) : null}
        </div>
        {t.drafts ? <Drafts t={t} /> : t.videos && t.videos.length > 1 ? (
          <div class="tk-vids">
            {t.videos.map((v) => (
              <span class={`tk-vid${v.done ? ' is-done' : ''}`} key={v.id}>
                {v.done ? '✓ ' : ''}
                {v.name}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <div class="tk-right">
        {t.state === 'mine' ? <span class="tk-tag">{x.yourTurn}</span> : null}
        {t.who.length && t.state !== 'not_relevant' ? <AvatarStack people={t.who} max={3} size="sm" /> : null}
        {has('yes') ? (
          <>
            <button type="button" class="btn btn-sm" onClick={() => p.act(t.key, 'yes')}>
              {x.yes}
            </button>
            <button type="button" class="btn btn-sm" onClick={() => p.act(t.key, 'no')}>
              {x.no}
            </button>
          </>
        ) : null}
        {has('done') ? (
          <button type="button" class="btn btn-sm" onClick={() => p.act(t.key, 'done')}>
            {x.done}
          </button>
        ) : null}
        {has('not_relevant') ? <RowMenu label={x.rowMenu(t.title)} items={[{ label: x.notRelevant, run: () => p.act(t.key, 'not_relevant') }]} /> : null}
      </div>
    </li>
  );
}

/** The open step's tasks: the viewer's own first, then the others. */
export function StageTaskPanel(p: { stage: StageTasks; title: string; text: string; list: TaskList; act: (key: string, a: Action | 'on' | 'off') => void }) {
  const lang = useLang();
  const x = T[lang];
  const mine = p.stage.tasks.filter((t) => t.state === 'mine' || (t.state === 'question' && t.actions.includes('yes')));
  const rest = p.stage.tasks.filter((t) => !mine.includes(t));
  const optional = p.list.optional.filter((o) => OPTIONAL_STAGE[o.key] === p.stage.stage);
  return (
    <div class="tk-panel" id={`tk-${p.stage.stage}`}>
      <div class="tk-head">
        <h3>{p.title}</h3>
        <span class="small muted">
          {x.count(p.stage.done, p.stage.total)} · {p.text}
        </span>
      </div>
      {mine.length && rest.length ? <h4 class="tk-sub">{x.yourTasks}</h4> : null}
      {mine.length ? (
        <ul class="tk-list">
          {mine.map((t) => (
            <TaskRow t={t} act={p.act} key={t.key} />
          ))}
        </ul>
      ) : null}
      {mine.length && rest.length ? <h4 class="tk-sub">{x.others}</h4> : null}
      {rest.length ? (
        <ul class="tk-list">
          {rest.map((t) => (
            <TaskRow t={t} act={p.act} key={t.key} />
          ))}
        </ul>
      ) : null}
      {optional.length ? (
        <MoreOptions id="stage-tasks">
          <p class="small muted">{x.optionalHint}</p>
          <div class="tk-optional">
            {optional.map((o) => (
              <label class="check" key={o.key}>
                <input type="checkbox" checked={o.on} onChange={() => p.act(o.key, o.on ? 'off' : 'on')} />
                {o.title}
              </label>
            ))}
          </div>
        </MoreOptions>
      ) : null}
      <div class="tk-legend small muted" aria-hidden="true">
        <span>
          <span class="tk-box tk-auto tk-on">✓</span>
          {x.legendAuto}
        </span>
        <span>
          <span class="tk-box tk-on">✓</span>
          {x.legendHand}
        </span>
        <span>
          <span class="tk-box tk-auto tk-part">1/3</span>
          {x.legendPart}
        </span>
        <span>
          <span class="tk-box tk-na">–</span>
          {x.legendNa}
        </span>
      </div>
    </div>
  );
}
const OPTIONAL_STAGE: Record<string, TaskStage> = { kickoff: 'planning', planningMeeting: 'planning', ideas: 'planning', feedbackMeeting: 'delivered', renewal: 'delivered' };

/** "3 av 5 klara" under a step. */
export const stageCount = (s: StageTasks | undefined, lang: 'en' | 'sv', short = false) => (s && s.total ? (short ? T[lang].countShort(s.done, s.total) : T[lang].count(s.done, s.total)) : null);

interface MyTask {
  projectId: string;
  projectName: string;
  client: string;
  key: string;
  stage: TaskStage;
  title: string;
  question: string | null;
  detail: string | null;
}

/** My work: the project tasks that are the viewer's turn (none = nothing shown). */
export function MyTasks(p: { api: Api }) {
  const lang = useLang();
  const x = T[lang];
  const [rows, setRows] = useState<MyTask[]>(() => peek<{ tasks: MyTask[] }>(p.api, '/api/my-tasks')?.tasks ?? []);
  useEffect(() => {
    p.api.get<{ tasks: MyTask[] }>('/api/my-tasks').then(
      (r) => setRows(r.tasks),
      () => setRows([])
    );
  }, []);
  // folded by default with its count and how many questions wait (owner 2026-11-02); open shows the first rows
  const [open, toggle] = useFold('tasks', false);
  const [all, setAll] = useState(false);
  if (!rows.length) return null;
  const questions = rows.filter((r) => r.question).length;
  return (
    <section class="panel mw-tasks" aria-labelledby="mw-tasks-h">
      <FoldHead id="mw-tasks-h" title={x.myTitle} n={rows.length} hint={questions ? x.nQuestions(questions) : null} open={open} toggle={toggle} />
      {open ? (
      <div id="mw-tasks-h-body">
      <ul class="tk-list">
        {(all ? rows : rows.slice(0, FIRST_TASKS)).map((r) => (
          <li class="tk-row is-mine" key={`${r.projectId}:${r.key}`}>
            <span class={`tk-box${r.question ? ' tk-q' : HAND.has(r.key) ? '' : ' tk-auto'}`} aria-hidden="true">
              {r.question ? '?' : ''}
            </span>
            <div class="tk-body">
              <div class="tk-title">
                <a
                  href={`/projects/${r.projectId}?step=${r.stage}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/projects/${r.projectId}?step=${r.stage}`);
                  }}
                >
                  {r.question ?? r.title}
                </a>
              </div>
              <div class="tk-meta">{[`${r.client} · ${r.projectName}`, r.detail].filter(Boolean).join(' · ')}</div>
            </div>
            <div class="tk-right">
              <span class="tk-tag">{r.question ? x.answer : x.yourTurn}</span>
            </div>
          </li>
        ))}
      </ul>
      {rows.length > FIRST_TASKS ? (
        <div class="mw-more">
          <button type="button" class="btn btn-ghost" aria-expanded={all} onClick={() => setAll(!all)}>
            {all ? x.showFewer : x.showAll(rows.length)}
          </button>
        </div>
      ) : null}
      </div>
      ) : null}
    </section>
  );
}
const FIRST_TASKS = 5;
